import {useAuth} from '@apps-common/auth-react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BusinessIcon from '@mui/icons-material/Business';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Chip,
  ClickAwayListener,
  Grow,
  IconButton,
  InputBase,
  LinearProgress,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography,
  Zoom,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {styled} from '@mui/material/styles';
import {keepPreviousData} from '@tanstack/react-query';
import {useDebounce} from '@uidotdev/usehooks';
import uniqBy from 'lodash/uniqBy';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {trackEvent} from '@app/analytics';
import {useGetOrganization, useGetOrganizations} from '@app/api/organizations';
import {TOUR_STAGES} from '@app/constants';
import useOrgParams from '@app/hooks/useOrgParams';
import {isPlanetAdmin} from '@app/util/user';

function PopperComponent(props) {
  const {disablePortal, anchorEl, open, ...other} = props;
  return <div {...other} />;
}

PopperComponent.propTypes = {
  anchorEl: PropTypes.any,
  disablePortal: PropTypes.bool,
  open: PropTypes.bool.isRequired,
};

const StyledInput = styled(InputBase)(({theme}) => ({
  padding: 10,
  width: '100%',
  borderBottom: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  '& input': {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#fff',
    ...theme.applyStyles('dark', {
      backgroundColor: (theme.vars || theme).palette.grey[900],
    }),
    padding: theme.spacing(1),
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    border: `1px solid ${(theme.vars || theme).palette.divider}`,
    fontSize: theme.typography.body2.fontSize,
    '&:focus': {
      borderColor: (theme.vars || theme).palette.primary.main,
      ...theme.applyStyles('dark', {
        borderColor: (theme.vars || theme).palette.primary.dark,
      }),
    },
  },
}));

const OrgSwitcher = () => {
  const {user, signOut} = useAuth();
  const {orgId: selectedOrgId, setOrg} = useOrgParams();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef();

  const [selectedOrganization, setSelectedOrganization] = useState();

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 250);

  const userIsPlanetAdmin = useMemo(() => isPlanetAdmin(user?.claims), [user]);

  // Keep the user's org info at all times regardless if Org List is filtered
  const {
    data: userOrgData,
    isLoading: userOrgLoading,
    error: ownOrgError,
  } = useGetOrganization(
    {
      id: user?.claims.organization_id,
    },
    {enabled: !!user?.claims.organization_id, refetchOnWindowFocus: true}
  );

  useEffect(() => {
    // log the user out if they get a 403 with their own org
    // this probably means access was revoked or api key was rotated
    if (ownOrgError?.response?.status === 403) {
      trackEvent('Automatically signed out user');
      signOut();
    }
  }, [ownOrgError, signOut]);

  // Look up specific org if loaded via query param
  // since it may not be part of the first page of results in orgs list
  const {
    data: selectedOrgData,
    isLoading: selectedOrgLoading,
    isSuccess: selectedOrgSuccess,
    isError: selectedOrgError,
  } = useGetOrganization(
    {
      id: selectedOrgId,
    },
    {
      enabled: !!selectedOrgId,
    }
  );

  // fetch first page of orgs for admins, sorted by newest first
  const {data: organizations, isFetching: loadingOrganizations} =
    useGetOrganizations(
      {
        search: debouncedQuery || undefined,
      },
      {
        enabled: userIsPlanetAdmin,
        placeholderData: keepPreviousData,
        select: data => data?.results || [],
      }
    );

  const canSelectOrgs = userIsPlanetAdmin || organizations?.length > 1;

  const handleClick = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const onFilterChange = (event, newValue, reason) => {
    if (reason !== 'input') {
      return;
    }
    setQuery(newValue);
  };

  // Build the options list, keeping the user's org at the top
  const allOrganizations = useMemo(() => {
    if (organizations && selectedOrganization) {
      const temp = uniqBy([userOrgData, ...organizations], 'id');
      if (!temp.find(org => org?.id === selectedOrganization.id)) {
        temp.splice(1, 0, selectedOrganization);
      }
      return temp;
    }
    return [];
  }, [organizations, selectedOrganization, userOrgData]);

  useEffect(() => {
    // have all needed data
    if (!selectedOrgLoading && !userOrgLoading && !selectedOrganization) {
      // default to the user's org
      let newOrg = userOrgData;
      // if there was a selected org from queryParam go with that
      if (selectedOrgId && selectedOrgData) {
        newOrg = selectedOrgData;
      }

      // only set the query param if there is none
      if (!selectedOrgId && newOrg) {
        setOrg(newOrg.id);
      }

      setSelectedOrganization(newOrg);
    }
  }, [
    selectedOrgData,
    selectedOrgError,
    selectedOrgLoading,
    selectedOrgSuccess,
    selectedOrganization,
    userOrgData,
    userOrgLoading,
    setOrg,
    selectedOrgId,
  ]);

  if (!user) {
    return false;
  }

  return (
    <>
      <Zoom in={Boolean(selectedOrganization)}>
        <Button
          ref={anchorRef}
          disabled={!canSelectOrgs}
          size="small"
          startIcon={<BusinessIcon />}
          endIcon={canSelectOrgs && <ArrowDropDownIcon />}
          variant="outlined"
          onClick={handleClick}
          sx={{
            marginRight: 1,
            maxWidth: 300,
            minWidth: 150,
            justifyContent: 'flex-start',
            color: 'inherit',
            textTransform: 'none',
            fontWeight: 600,
            '&.Mui-disabled': {
              color: 'inherit',
            },
          }}
          data-tour={TOUR_STAGES.ORG_SWITCHER}
        >
          <Typography noWrap variant="inherit" sx={{flex: 1}} align="left">
            {selectedOrganization?.name}
          </Typography>
        </Button>
      </Zoom>
      <Popper
        id={open ? 'org-switcher' : undefined}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-end"
        transition
        disablePortal
        sx={{
          zIndex: 'modal',
        }}
      >
        {({TransitionProps}) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'top right',
            }}
          >
            <Paper
              elevation={4}
              sx={{
                width: 300,
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <div>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',

                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      padding: theme => theme.spacing(1, 1.25),
                    }}
                  >
                    <Typography variant="subtitle2">
                      Select an organization
                    </Typography>
                    <IconButton size="small" onClick={handleClose}>
                      <CloseIcon fontSize="small" sx={{fontSize: 14}} />
                    </IconButton>
                  </Stack>
                  <Autocomplete
                    open
                    freeSolo
                    onClose={(_, reason) => {
                      if (reason === 'escape') {
                        handleClose();
                      }
                    }}
                    value={selectedOrganization}
                    inputValue={query}
                    onInputChange={onFilterChange}
                    filterOptions={x => x}
                    onChange={(_, newValue) => {
                      if (newValue) {
                        setSelectedOrganization(newValue);
                        setOrg(newValue.id);
                      }
                    }}
                    disableCloseOnSelect
                    slots={{
                      popper: PopperComponent,
                    }}
                    slotProps={{
                      paper: {
                        elevation: 4,
                        sx: {
                          boxShadow: 'none',
                          margin: 0,
                          borderRadius: '0 0 4px 4px',
                        },
                      },
                      listbox: {
                        sx: {
                          padding: 0,
                        },
                      },
                    }}
                    noOptionsText="No organizations"
                    renderOption={({key, ...props}, option, {selected}) => {
                      return (
                        <ListItem
                          key={key}
                          {...props}
                          dense
                          divider
                          sx={{
                            '&.MuiAutocomplete-option': {
                              paddingX: 1,
                            },
                          }}
                        >
                          {selected && (
                            <ListItemIcon
                              sx={{minWidth: theme => theme.spacing(4)}}
                            >
                              <CheckIcon fontSize="small" />
                            </ListItemIcon>
                          )}
                          <ListItemText
                            inset={!selected}
                            disableTypography
                            sx={{
                              paddingLeft: !selected ? 4 : 0,
                            }}
                          >
                            <Stack
                              spacing={1}
                              direction="row"
                              sx={{
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography variant="body2" noWrap>
                                {option.name}
                              </Typography>
                              {userOrgData.id === option.id && (
                                <Chip
                                  size="xsmall"
                                  label="Your Org"
                                  sx={{marginLeft: 'auto'}}
                                />
                              )}
                            </Stack>
                          </ListItemText>
                        </ListItem>
                      );
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return option.id === value.id;
                    }}
                    options={allOrganizations}
                    getOptionLabel={option => {
                      return option.name || '';
                    }}
                    renderInput={params => (
                      <>
                        <StyledInput
                          ref={params.InputProps.ref}
                          inputProps={params.inputProps}
                          autoFocus
                          placeholder="Filter organizations"
                        />
                        {loadingOrganizations && (
                          <LinearProgress
                            sx={{
                              height: 2,
                              zIndex: 'speedDial',
                              backgroundColor: 'grey.200',
                            }}
                          />
                        )}
                      </>
                    )}
                  />
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default OrgSwitcher;
